import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEntity } from '../../contexts/EntityContext';
import { api, DebtItem } from '../../lib/api';
import Modal from '../../components/ui/Modal';
import { LoadingState } from '../../components/ui/Spinner';
import { useToast } from '../../components/ui/Toast';
import { formatAmount, formatDate, calculateDisplayRate, formatDisplayRate } from '../../lib/format';

function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export default function DebtPage() {
  const { t } = useTranslation(['reports', 'common']);
  const { currentEntity } = useEntity();
  const [selectedDebt, setSelectedDebt] = useState<DebtItem | null>(null);
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['debt', currentEntity?.id, page],
    queryFn: () => api.debt.list(currentEntity!.id, { page, limit: 50 }),
    enabled: !!currentEntity,
  });

  if (!currentEntity) return null;

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">{t('columns.debt')}</h1>
      </div>

      {data?.totals && data.totals.length > 0 && (
        <div className="card mb-4">
          <h3 className="card-title mb-4">{t('cashflow.total')}</h3>
          <div className="debt-totals">
            {data.totals.map((tot) => (
              <div key={tot.currency} className="debt-total-item">
                <span className="debt-currency">{tot.currency}</span>
                <span className="debt-amount">{formatAmount(tot.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {isLoading ? (
        <LoadingState message={t('cashflow.loading')} />
      ) : data?.data.length === 0 ? (
        <div className="empty-debt-container">
          <div className="empty-debt-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8"/>
              <path d="M12 18V6"/>
            </svg>
          </div>
          <h3 className="empty-debt-title">{t('common:noData', 'No Pending Debts')}</h3>
          <p className="empty-debt-description">{t('common:noDataDescription', 'All transactions are settled. New pending deals will appear here.')}</p>
        </div>
      ) : (
        <>
          <div className="card">
            <div className="table-scroll">
              <table className="table table-compact">
                <thead>
                  <tr>
                    <th>{t('columns.date')}</th>
                    <th>{t('columns.type')}</th>
                    <th>{t('columns.client')}</th>
                    <th className="text-right">{t('columns.amountIn')}</th>
                    <th className="text-right">{t('columns.amountOut')}</th>
                    <th className="text-right">{t('columns.rate')}</th>
                    <th>{t('columns.status')}</th>
                    <th className="text-right">{t('columns.debt')}</th>
                    <th>{t('columns.employee')}</th>
                    <th>{t('columns.comment')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {data?.data.map((item) => {
                    // Determine debt direction - who owes whom
                    // If we received money (amountIn) but haven't paid out yet, CLIENT owes US
                    // If we paid out (amountOut) but didn't receive expected, WE owe CLIENT
                    const hasAmountIn = item.amountIn && parseFloat(item.amountIn) > 0;
                    const hasAmountOut = item.amountOut && parseFloat(item.amountOut) > 0;
                    const debtDirection = hasAmountIn && !hasAmountOut ? 'client-owes' : 'we-owe';

                    return (
                      <tr key={item.id} className="row-open">
                        <td className="text-nowrap">{formatDate(item.transactionDate || item.createdAt)}</td>
                        <td>
                          <span className={`badge badge-${item.type}`}>{capitalize(item.type)}</span>
                        </td>
                        <td>
                          <div className="client-cell">
                            <span>{item.clientName || '-'}</span>
                            <span className={`debt-direction ${debtDirection}`}>
                              {debtDirection === 'client-owes' ? '← owes us' : '→ we owe'}
                            </span>
                          </div>
                        </td>
                        <td className="text-right font-mono text-success">
                          {item.amountIn
                            ? `${formatAmount(item.amountIn, item.currencyIn || '')} ${item.currencyIn || ''}`
                            : '-'}
                        </td>
                        <td className="text-right font-mono text-error">
                          {item.amountOut
                            ? `${formatAmount(item.amountOut, item.currencyOut || '')} ${item.currencyOut || ''}`
                            : item.expectedAmount
                              ? `${formatAmount(item.expectedAmount)} (expected)`
                              : '-'}
                        </td>
                        <td className="text-right font-mono">
                          {formatDisplayRate(calculateDisplayRate(item))}
                        </td>
                        <td>
                          <span className="badge badge-open">{capitalize(item.status)}</span>
                        </td>
                        <td className="text-right font-mono text-error font-bold">
                          {item.debtAmount ? `${formatAmount(item.debtAmount)}` : '-'}
                        </td>
                        <td>{item.employeeName}</td>
                        <td className="comment-cell" title={item.comment || ''}>
                          {item.comment || '-'}
                        </td>
                        <td className="text-right actions-cell">
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => setSelectedDebt(item)}
                          >
                            Pay
                          </button>
                          <Link to={`/transactions/${item.id}`} className="btn btn-sm btn-secondary">
                            View
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {(data?.meta?.totalPages ?? 0) > 1 && (
            <div className="pagination">
              <button
                className="btn btn-secondary"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                &larr; {t('pagination.prev')}
              </button>
              <span>
                {t('pagination.page')} {page} {t('pagination.of')} {data?.meta?.totalPages}
              </span>
              <button
                className="btn btn-secondary"
                disabled={page >= (data?.meta?.totalPages ?? 0)}
                onClick={() => setPage(page + 1)}
              >
                {t('pagination.next')} &rarr;
              </button>
            </div>
          )}
        </>
      )}

      {selectedDebt && (
        <PaymentModal
          debt={selectedDebt}
          entityId={currentEntity.id}
          onClose={() => setSelectedDebt(null)}
        />
      )}

      <style>{`
        .debt-totals {
          display: flex;
          gap: 32px;
        }
        .debt-total-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .debt-currency {
          font-size: 12px;
          color: var(--color-gray-500);
        }
        .debt-amount {
          font-size: 24px;
          font-weight: 600;
          color: var(--color-error);
          font-family: monospace;
        }
        .table-scroll {
          overflow-x: auto;
        }
        .table-compact th,
        .table-compact td {
          padding: 8px 12px;
          font-size: 13px;
        }
        .text-nowrap {
          white-space: nowrap;
        }
        .font-mono {
          font-family: monospace;
        }
        .font-bold {
          font-weight: 600;
        }
        .row-open {
          background: var(--badge-open-bg);
        }
        .comment-cell {
          max-width: 120px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .client-cell {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .debt-direction {
          font-size: 10px;
          font-weight: 500;
        }
        .debt-direction.client-owes {
          color: var(--color-success);
        }
        .debt-direction.we-owe {
          color: var(--color-error);
        }
        .actions-cell {
          display: flex;
          gap: 4px;
          white-space: nowrap;
        }
        .btn-sm {
          padding: 4px 8px;
          font-size: 12px;
        }
        .pagination {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 16px;
          margin-top: 16px;
        }
        .badge-exchange {
          background: #dbeafe;
          color: #1e40af;
        }
        .badge-open {
          background: var(--badge-open-bg);
          color: var(--badge-open-text);
        }
        .empty-debt-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 64px 24px;
          background: var(--color-surface);
          border: 1px solid var(--color-border);
          border-radius: 12px;
          text-align: center;
        }
        .empty-debt-icon {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: linear-gradient(135deg, var(--color-success) 0%, #059669 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 24px;
          color: white;
          box-shadow: 0 8px 24px rgba(16, 185, 129, 0.25);
        }
        .empty-debt-title {
          font-size: 20px;
          font-weight: 600;
          color: var(--color-text);
          margin: 0 0 8px 0;
        }
        .empty-debt-description {
          font-size: 14px;
          color: var(--color-text-muted);
          margin: 0;
          max-width: 320px;
          line-height: 1.5;
        }
      `}</style>
    </div>
  );
}

function PaymentModal({
  debt,
  entityId,
  onClose,
}: {
  debt: DebtItem;
  entityId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation(['reports', 'common']);
  const queryClient = useQueryClient();
  const { success, error: showError } = useToast();
  const [error, setError] = useState<string | null>(null);
  const [direction, setDirection] = useState<'in' | 'out'>(() =>
    parseFloat(debt.debtAmountOut || '0') > 0 ? 'out' : 'in'
  );

  const { data: wallets } = useQuery({
    queryKey: ['wallets', entityId],
    queryFn: () => api.admin.wallets.list(entityId),
  });

  const { data: debtDetail } = useQuery({
    queryKey: ['debt-detail', entityId, debt.id],
    queryFn: () => api.debt.get(entityId, debt.id),
  });

  const mutation = useMutation({
    mutationFn: (data: any) => api.debt.addPayment(entityId, data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['debt'] });
      queryClient.invalidateQueries({ queryKey: ['debt-detail'] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });

      if (result.transactionClosed) {
        success(t('common:success'));
      } else {
        const rem = direction === 'out' ? result.remainingOut : result.remainingIn;
        success(`${t('common:success')} - ${rem} remaining`);
      }
      onClose();
    },
    onError: (err: any) => {
      setError(err.message || t('common:error'));
      showError(err.message || t('common:error'));
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    const formData = new FormData(e.currentTarget);
    const currency = direction === 'out'
      ? debtDetail?.summary.currencyOut || ''
      : debtDetail?.summary.currencyIn || '';

    mutation.mutate({
      transactionId: debt.id,
      direction,
      amount: formData.get('amount'),
      currency,
      walletId: formData.get('walletId') || undefined,
      comment: formData.get('comment') || undefined,
    });
  };

  // Pick currency and remaining based on chosen direction
  const currency = direction === 'out'
    ? debtDetail?.summary.currencyOut || ''
    : debtDetail?.summary.currencyIn || '';
  const remaining = direction === 'out'
    ? debtDetail?.summary.remainingOut || debt.debtAmountOut
    : debtDetail?.summary.remainingIn  || debt.debtAmountIn;
  const totalPaid = direction === 'out'
    ? debtDetail?.summary.totalPaidOut
    : debtDetail?.summary.totalPaidIn;

  const hasOut = parseFloat(debt.debtAmountOut || '0') > 0;
  const hasIn  = parseFloat(debt.debtAmountIn  || '0') > 0;

  return (
    <Modal isOpen={true} onClose={onClose} title={t('common:addPayment') || 'Record Payment'} size="md">
      <form onSubmit={handleSubmit}>
        {error && <div className="form-error mb-4">{error}</div>}

        {/* Direction selector — only show if both legs active */}
        {hasOut && hasIn && (
          <div className="form-group mb-4">
            <label className="form-label">Direction</label>
            <div className="flex gap-3">
              <button
                type="button"
                className={`btn flex-1 ${direction === 'out' ? 'btn-danger' : 'btn-secondary'}`}
                onClick={() => setDirection('out')}
              >
                📤 Pay out to client ({debt.currencyOut})
              </button>
              <button
                type="button"
                className={`btn flex-1 ${direction === 'in' ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setDirection('in')}
              >
                📥 Receive from client ({debt.currencyIn})
              </button>
            </div>
          </div>
        )}

        <div className="payment-summary mb-4">
          <div className="summary-item">
            <span className="summary-label">{t('columns.debt')}</span>
            <span className="summary-value text-error">
              {remaining} {currency}
            </span>
          </div>
          {totalPaid && parseFloat(totalPaid) > 0 && (
            <div className="summary-item">
              <span className="summary-label">{t('common:paid') || 'Paid'}</span>
              <span className="summary-value text-success">
                {totalPaid} {currency}
              </span>
            </div>
          )}
        </div>

        {debtDetail?.payments && debtDetail.payments.length > 0 && (
          <div className="payment-history mb-4">
            <h4>{t('common:history') || 'Payment History'}</h4>
            <table className="table">
              <thead>
                <tr>
                  <th>{t('columns.date')}</th>
                  <th>Dir</th>
                  <th>{t('columns.amount')}</th>
                  <th>{t('columns.employee')}</th>
                </tr>
              </thead>
              <tbody>
                {debtDetail.payments.map((p: any) => (
                  <tr key={p.id}>
                    <td>{formatDate(p.paymentDate)}</td>
                    <td>{p.direction === 'in' ? '📥' : '📤'}</td>
                    <td>
                      {formatAmount(p.amount, p.currency)} {p.currency}
                    </td>
                    <td>{p.recordedByName}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="form-group">
          <label className="form-label">
            {t('columns.amount')} ({currency})
          </label>
          <input
            name="amount"
            type="text"
            className="form-input"
            placeholder={`Max: ${remaining}`}
            required
            autoFocus
          />
        </div>

        <div className="form-group">
          <label className="form-label">{t('columns.wallet')}</label>
          <select name="walletId" className="form-input">
            <option value="">Select...</option>
            {wallets?.data
              .filter((w) => w.currencyCode === currency)
              .map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name} ({w.type})
                </option>
              ))}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">{t('columns.comment')}</label>
          <input name="comment" type="text" className="form-input" />
        </div>

        <div className="flex gap-2 justify-end">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            {t('common:cancel')}
          </button>
          <button type="submit" className="btn btn-primary" disabled={mutation.isPending}>
            {mutation.isPending ? '...' : t('common:save')}
          </button>
        </div>
      </form>

      <style>{`
        .payment-summary {
          background: var(--color-gray-50);
          padding: 16px;
          border-radius: var(--radius);
          display: flex;
          gap: 24px;
        }
        .summary-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .summary-label {
          font-size: 12px;
          color: var(--color-gray-500);
        }
        .summary-value {
          font-size: 18px;
          font-weight: 600;
          font-family: monospace;
        }
        .payment-history h4 {
          font-size: 14px;
          margin-bottom: 8px;
        }
        .payment-history .table {
          font-size: 13px;
        }
        .payment-history .table th,
        .payment-history .table td {
          padding: 8px;
        }
      `}</style>
    </Modal>
  );
}
